import React, { useState } from 'react';
import ConfirmModal from './ConfirmModal';
import './EntryCard.css';

function EntryCard({ entry, onDelete, onEdit, onPrivacyToggle, username }) {
  const [deleting, setDeleting] = useState(false);
  const [togglingPrivacy, setTogglingPrivacy] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = () => {
    setShowDeleteModal(false);
    setDeleting(true);
    // Just notify parent — parent handles backend call and state update
    onDelete(entry.id, () => setDeleting(false));
  };

  const handlePrivacyClick = () => {
    setShowPrivacyModal(true);
  };

  const handlePrivacyConfirm = async () => {
    setShowPrivacyModal(false);
    try {
      setTogglingPrivacy(true);
      const currentUsername = username || localStorage.getItem('username');
      const response = await fetch(
        `http://localhost:8080/api/entries/${entry.id}/toggle-privacy?username=${currentUsername}`,
        { method: 'PATCH' }
      );
      const data = await response.json();
      if (data.success && onPrivacyToggle) {
        onPrivacyToggle(entry.id, data.isPrivate);
      } else {
        setErrorMessage('Failed to update privacy. Please try again.');
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error('Error toggling privacy:', error);
      setErrorMessage('Failed to update privacy. Please try again.');
      setShowErrorModal(true);
    } finally {
      setTogglingPrivacy(false);
    }
  };

  const handleEdit = () => {
    onEdit(entry.id);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getRatingClass = (rating) => {
    if (rating >= 8) return 'rating-excellent';
    if (rating >= 5) return 'rating-average';
    return 'rating-poor';
  };

  const getRatingLabel = (rating) => {
    if (rating >= 8) return 'Excellent';
    if (rating >= 5) return 'Average';
    return 'Needs Work';
  };

  const isPrivate = entry.isPrivate === true;

  return (
    <>
      <div className={`entry-card ${isPrivate ? 'private-entry' : ''}`}>
        {isPrivate && (
          <div className="private-badge">🔒 Private</div>
        )}

        <div className="entry-header">
          <div className="entry-user">
            <span className="user-icon">👤</span>
            <span className="user-name">{entry.partnerName}</span>
          </div>
          <span className="entry-date">{formatDate(entry.date)}</span>
        </div>

        <div className="entry-body">
          <div className="entry-category-badge">{entry.category}</div>
          <div className={`entry-rating ${getRatingClass(entry.rating)}`}>
            <span className="rating-value">{entry.rating}/10</span>
            <span className="rating-label">{getRatingLabel(entry.rating)}</span>
          </div>
          {entry.description && (
            <p className="entry-description">{entry.description}</p>
          )}
        </div>

        <div className="entry-footer">
          <button
            className="privacy-toggle-button"
            onClick={handlePrivacyClick}
            disabled={deleting || togglingPrivacy}
            title={isPrivate ? 'Make Public' : 'Make Private'}
          >
            {togglingPrivacy ? '⏳' : isPrivate ? '🔓 Make Public' : '🔒 Make Private'}
          </button>
          <button
            className="edit-button"
            onClick={handleEdit}
            disabled={deleting || togglingPrivacy}
          >
            ✏️ Edit
          </button>
          <button
            className="delete-button"
            onClick={handleDeleteClick}
            disabled={deleting || togglingPrivacy}
          >
            {deleting ? 'Deleting...' : '🗑️ Delete'}
          </button>
        </div>
      </div>

      <ConfirmModal
        isOpen={showDeleteModal}
        title="Delete Entry"
        message="This entry will be permanently deleted. This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setShowDeleteModal(false)}
      />

      <ConfirmModal
        isOpen={showPrivacyModal}
        title={isPrivate ? 'Make Entry Public' : 'Make Entry Private'}
        message={isPrivate
          ? 'This entry will be visible in your regular entries list.'
          : 'This entry will be hidden and require your PIN to view.'}
        confirmText={isPrivate ? 'Make Public' : 'Make Private'}
        cancelText="Cancel"
        variant={isPrivate ? 'info' : 'warning'}
        onConfirm={handlePrivacyConfirm}
        onCancel={() => setShowPrivacyModal(false)}
      />

      <ConfirmModal
        isOpen={showErrorModal}
        title="Something went wrong"
        message={errorMessage}
        confirmText="OK"
        variant="warning"
        onConfirm={() => setShowErrorModal(false)}
        onCancel={() => setShowErrorModal(false)}
      />
    </>
  );
}

export default EntryCard;